import { BillRepository } from '~/repositories/billRepository';
import { isBillPaymentValid } from './helpers';
import type { GetVendorBillsToPayParams, PayVendorBillsDto, PayVendorBillsGroupDto } from '~/swagger/portal/Api';
import type { BillPaymentSection, PayVendorBillsStoreData } from '~/types/payVendorBills';

const billRepo = new BillRepository();

function buildPayVendorBillsPayload(storeData: PayVendorBillsStoreData): PayVendorBillsDto {
  const filteredBillPayments = storeData.billPayments.filter(isBillPaymentValid);

  const findCommonData = (section: BillPaymentSection) => {
    const firstBill = filteredBillPayments.find((b) => b.vendor.id === section.vendorId && section.billIds.includes(b.id));
    if (!firstBill) return undefined;

    const bankAccount = storeData.bankAccounts.find((a) => a.id === firstBill.paymentBankAccountId);
    return {
      vendorId: section.vendorId,
      bankAccountId: firstBill.paymentBankAccountId!,
      cashChartOfAccountId: bankAccount?.chartOfAccountIds?.[0] ?? '',
      paymentType: firstBill.paymentType,
      externalTransactionId: firstBill.externalTransactionId,
      paymentMethod: firstBill.paymentMethod,
      paymentAccountId: firstBill.receivingBankAccountId,
      memo: firstBill.memo,
    };
  };

  const groups: PayVendorBillsGroupDto[] = [];

  for (const section of storeData.billPaymentSections) {
    const commonData = findCommonData(section);
    const payments = section.billIds
      .map((billId) => {
        const data = filteredBillPayments.find((b) => b.id === billId && b.vendor.id === section.vendorId);
        return data ? { billId, amount: data.paymentAmount! } : null;
      })
      .filter((p): p is { billId: string; amount: number } => p !== null);

    if (commonData && payments.length) {
      groups.push({ ...commonData, payments });
    }
  }

  return { paymentDate: storeData.paymentDate, groups };
}

export function useVendorBillsPortal() {
  const fetchBills = (filter?: GetVendorBillsToPayParams) => billRepo.getVendorBillsPay(filter);

  const payBills = async (storeData: PayVendorBillsStoreData) => {
    const payload = buildPayVendorBillsPayload(storeData);
    return billRepo.payBill(payload);
  };

  return { fetchBills, payBills };
}
